import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { useMemo } from 'react'
import { createContainer } from 'unstated-next'

import { gql } from '../../../../__generated__/graphql/catalog'
import { useNextQuery } from '../../../../app/_hooks/useNextQuery'

import { usePdpServerSideProps } from './PdpServerSidePropsProvider'

const pdpDeprecatedServerSidePropsQuery = gql(`
  query GetPdpZidiServerSideProps(
      $collectionHandle: String
      $productGroupHandle: String!
      $colorKey: String!
      $fitKey: String!
      $logoScope: LogoScopeEnum
  ) {
    productGroup(
      handle: $productGroupHandle
      collectionHandle: $collectionHandle
    ) {
      __typename
      id
      handle
      iconBadge
      embroiderable
      embroideryImages {
        id
        source
        color
        embroideryOffsets
        fit
        index
        kind
        type
      }
      featuredProduct(color: $colorKey, fit: $fitKey) {
        id
        __typename
        handle
        iconBadge
        embroidery(logoScope: $logoScope) {
          ...ProductEmbroidery
        }
        detailImages
        fitModel {
          description
          height
          images
          name
          pantsSize
          topSize
        }
        fitModelCount
        fitModelsDescriptions
        whyWeLoveIcons: iconList {
          id
          __typename
          image
          label
          title
        }
        images {
          nodes {
            id
            source
            color
            embroideryOffsets
            fit
            index
            kind
            type
            name
          }
        }
        videos {
          nodes {
            kind
            source
            type
          }
        }
      }
    }
  }
`)

/**
 * @deprecated use PdpServerSidePropsProvider exports instead of these Zidi based ones in this file.
 */
export type UsePdpDeprecatedServerSidePropsProps = {
  productGroupHandle: string
  colorKey: string | undefined
  fitKey: string | undefined
  collectionHandle: string | undefined
  includeUnavailable?: boolean
  isPreview?: boolean
  launchTags?: string
}

/**
 * @deprecated use PdpServerSidePropsProvider exports instead of these Zidi based ones in this file.
 */
export const primeClientWithPdpDeprecatedServerSideProps = ({
  client,
  props,
}: {
  client: ApolloClient<NormalizedCacheObject>
  props: UsePdpDeprecatedServerSidePropsProps
}) => {
  const {
    productGroupHandle,
    colorKey = '',
    fitKey = '',
    collectionHandle,
    includeUnavailable,
    isPreview,
    launchTags,
  } = props

  return client.query({
    errorPolicy: 'all',
    query: pdpDeprecatedServerSidePropsQuery,
    variables: {
      productGroupHandle,
      colorKey,
      fitKey,
      collectionHandle,
      logoScope: 'DTC',
    },
    context: { includeUnavailable, isPreview, launchTags },
  })
}

function usePdpDeprecatedServerSidePropsImpl(
  props: UsePdpDeprecatedServerSidePropsProps | undefined
) {
  const {
    productGroupHandle,
    colorKey = '',
    fitKey = '',
    collectionHandle,
    includeUnavailable,
    isPreview,
    launchTags,
  } = props!

  const { data: pdpServerSideProps } = usePdpServerSideProps()

  const queryResponse = useNextQuery(pdpDeprecatedServerSidePropsQuery, {
    variables: {
      productGroupHandle,
      colorKey,
      fitKey,
      collectionHandle,
      logoScope: pdpServerSideProps?.product.variantGroup.isPortalColor ? 'PORTALS' : 'DTC',
    },
    context: { includeUnavailable, isPreview, launchTags },
  })

  return useMemo(() => {
    return {
      ...queryResponse,
      data: queryResponse.data ?? queryResponse.previousData,
    }
  }, [queryResponse])
}

const PdpDeprecatedServerSidePropsContainer = createContainer(usePdpDeprecatedServerSidePropsImpl)
PdpDeprecatedServerSidePropsContainer.Provider.displayName = 'PdpDeprecatedServerSidePropsImpl'

/**
 * @deprecated use PdpServerSidePropsProvider exports instead of these Zidi based ones in this file.
 */
export const PdpDeprecatedServerSidePropsProvider = ({
  initialState,
  children,
}: {
  initialState: UsePdpDeprecatedServerSidePropsProps
  children: React.ReactNode
}) => {
  return (
    <PdpDeprecatedServerSidePropsContainer.Provider initialState={initialState}>
      {children}
    </PdpDeprecatedServerSidePropsContainer.Provider>
  )
}

/**
 * @deprecated use PdpServerSidePropsProvider exports instead of these Zidi based ones in this file.
 */
export const usePdpDeprecatedServerSideProps = PdpDeprecatedServerSidePropsContainer.useContainer
